import React, { useRef } from 'react';
import { Box, Typography, Container, IconButton, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { LightMode, DarkMode, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';

const PrivacyPolicy: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    cardBg: isDark ? '#1a1a1a' : '#ffffff',
    headerBg: isDark ? '#161616' : '#fafafa',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    border: isDark ? '#1a1a1a' : '#f0f0f0',
  };

  return (
    <>
      <SEO 
        title="Privacy Policy - Daily Stories"
        description="Privacy Policy for Daily Stories app. Learn how we collect, use, and protect your information."
        url="https://dailystories.fr/privacy-policy"
      />
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: colors.bg,
        py: { xs: 4, md: 6 }
      }}>
        {/* Content */}
        <Box
          ref={contentScrollRef}
          sx={{
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#333 transparent' : '#d0d0d0 transparent',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? '#333' : '#d0d0d0',
              borderRadius: '4px',
            },
          }}
        >
          <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
            <Box sx={{ color: colors.text }}>
              <Typography variant="h3" sx={{ mb: 1, fontWeight: 300 }}>
                PRIVACY POLICY
              </Typography>
              <Typography variant="body2" sx={{ mb: 4, color: colors.textSecondary }}>
                Last Updated: December 2024
              </Typography>

              <Section title="1. INTRODUCTION" colors={colors}>
                <Typography variant="body1" paragraph>
                  Plotcamp ("Company", "we", "our", or "us") operates Daily Stories ("the App"). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our App.
                </Typography>
                <Typography variant="body1" paragraph>
                  By using the App, you consent to the data practices described in this Privacy Policy. If you do not agree, please do not use the App.
                </Typography>
              </Section>

              <Section title="2. INFORMATION WE COLLECT" colors={colors}>
                <SubSection title="2.1 Information You Provide" colors={colors}>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>Age verification confirmation</li>
                      <li>App preferences and settings</li>
                    </ul>
                  </Typography>
                </SubSection>

                <SubSection title="2.2 Information Collected Automatically" colors={colors}>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>Device information (device type, operating system, unique device identifiers)</li>
                      <li>App usage data (stories viewed, reading time, features used)</li>
                      <li>Log data (access times, app crashes, system activity)</li>
                    </ul>
                  </Typography>
                </SubSection>

                <SubSection title="2.3 Subscription Information" colors={colors}>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>Subscription status and type</li>
                      <li>Purchase history (processed through Apple/Google)</li>
                      <li>Subscription start and end dates</li>
                    </ul>
                  </Typography>
                </SubSection>

                <Typography variant="body1" paragraph sx={{ mt: 2, fontWeight: 500 }}>
                  Note: We do NOT collect or store:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Your name or email address (unless you contact us)</li>
                    <li>Payment card details (handled entirely by Apple/Google)</li>
                    <li>Location data</li>
                    <li>Contacts or photos</li>
                  </ul>
                </Typography>
              </Section>

              <Section title="3. HOW WE USE YOUR INFORMATION" colors={colors}>
                <Typography variant="body1" paragraph>
                  We use the information we collect to:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Provide, maintain, and improve the App</li>
                    <li>Process and manage your subscription</li>
                    <li>Personalize your experience (theme preferences, reading history)</li>
                    <li>Analyze usage patterns to improve content and features</li>
                    <li>Detect and prevent fraud or abuse</li>
                    <li>Comply with legal obligations</li>
                    <li>Communicate important updates about the App or your subscription</li>
                  </ul>
                </Typography>
              </Section>

              <Section title="4. SUBSCRIPTION DATA" colors={colors}>
                <SubSection title="4.1 Payment Processing" colors={colors}>
                  <Typography variant="body1" paragraph>
                    All payments are processed through Apple App Store or Google Play. We never receive or store your payment card information. We only receive confirmation of your subscription status.
                  </Typography>
                </SubSection>

                <SubSection title="4.2 Subscription Management" colors={colors}>
                  <Typography variant="body1" paragraph>
                    Your subscription is managed through your Apple ID or Google Play account. We receive:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>Whether you have an active subscription</li>
                      <li>Subscription type and expiration date</li>
                      <li>Whether you're in a trial period</li>
                    </ul>
                  </Typography>
                </SubSection>

                <SubSection title="4.3 RevenueCat" colors={colors}>
                  <Typography variant="body1" paragraph>
                    We use RevenueCat to manage subscriptions across platforms. RevenueCat receives:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>Anonymous user identifier</li>
                      <li>Subscription purchase events</li>
                      <li>Device type and app version</li>
                    </ul>
                  </Typography>
                  <Typography variant="body1" paragraph>
                    RevenueCat's privacy policy: <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: colors.text }}>https://www.revenuecat.com/privacy</a>
                  </Typography>
                </SubSection>
              </Section>

              <Section title="5. DATA STORAGE AND SECURITY" colors={colors}>
                <SubSection title="5.1 Local Storage" colors={colors}>
                  <Typography variant="body1" paragraph>
                    Most of your data (age verification, preferences, reading progress) is stored locally on your device using secure storage.
                  </Typography>
                </SubSection>

                <SubSection title="5.2 Security Measures" colors={colors}>
                  <Typography variant="body1" paragraph>
                    We implement industry-standard security measures including:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>Encryption of data in transit (HTTPS/TLS)</li>
                      <li>Secure local storage</li>
                      <li>Regular security assessments</li>
                    </ul>
                  </Typography>
                </SubSection>

                <SubSection title="5.3 Data Breach Notification" colors={colors}>
                  <Typography variant="body1" paragraph>
                    In the event of a data breach affecting your personal information, we will notify you and relevant authorities as required by law.
                  </Typography>
                </SubSection>
              </Section>

              <Section title="6. DATA SHARING AND DISCLOSURE" colors={colors}>
                <Typography variant="body1" paragraph>
                  We do not sell your personal information. We may share data with:
                </Typography>

                <SubSection title="6.1 Service Providers" colors={colors}>
                  <Typography variant="body1" component="div">
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                      <li>RevenueCat (subscription management)</li>
                      <li>Apple/Google (payment processing)</li>
                      <li>Analytics providers (anonymized usage data)</li>
                    </ul>
                  </Typography>
                </SubSection>

                <SubSection title="6.2 Legal Requirements" colors={colors}>
                  <Typography variant="body1" paragraph>
                    We may disclose information if required by law, court order, or government request.
                  </Typography>
                </SubSection>

                <SubSection title="6.3 Business Transfers" colors={colors}>
                  <Typography variant="body1" paragraph>
                    In the event of a merger, acquisition, or sale, your information may be transferred to the new owner.
                  </Typography>
                </SubSection>
              </Section>

              <Section title="7. YOUR RIGHTS AND CHOICES" colors={colors}>
                <SubSection title="7.1 Access and Portability" colors={colors}>
                  <Typography variant="body1" paragraph>
                    You can request a copy of your personal data by contacting us.
                  </Typography>
                </SubSection>

                <SubSection title="7.2 Deletion" colors={colors}>
                  <Typography variant="body1" paragraph>
                    You can request deletion of your data. Note that some data may be retained for legal or legitimate business purposes.
                  </Typography>
                </SubSection>

                <SubSection title="7.3 Opt-Out" colors={colors}>
                  <Typography variant="body1" paragraph>
                    You can opt out of analytics collection in the App settings.
                  </Typography>
                </SubSection>

                <SubSection title="7.4 Subscription Cancellation" colors={colors}>
                  <Typography variant="body1" paragraph>
                    You can cancel your subscription at any time through Apple App Store or Google Play settings.
                  </Typography>
                </SubSection>
              </Section>

              <Section title="8. DATA RETENTION" colors={colors}>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Subscription data: Retained while subscription is active plus 90 days</li>
                    <li>Usage analytics: Retained for 24 months in anonymized form</li>
                    <li>Local preferences: Retained until you delete the App</li>
                  </ul>
                </Typography>
              </Section>

              <Section title="9. CHILDREN'S PRIVACY" colors={colors}>
                <Typography variant="body1" paragraph>
                  This App is strictly for users 18 years of age and older. We do not knowingly collect information from anyone under 18. If we discover we have collected data from someone under 18, we will delete it immediately and terminate their access.
                </Typography>
              </Section>

              <Section title="10. INTERNATIONAL DATA TRANSFERS" colors={colors}>
                <Typography variant="body1" paragraph>
                  Your information may be transferred to and processed in the United States. By using the App, you consent to this transfer.
                </Typography>
              </Section>

              <Section title="11. CALIFORNIA PRIVACY RIGHTS (CCPA)" colors={colors}>
                <Typography variant="body1" paragraph>
                  California residents have the right to:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Know what personal information we collect</li>
                    <li>Request deletion of personal information</li>
                    <li>Opt out of the sale of personal information (we do not sell data)</li>
                    <li>Non-discrimination for exercising privacy rights</li>
                  </ul>
                </Typography>
                <Typography variant="body1" paragraph>
                  To exercise these rights, contact us at cem@plotcamp.com.
                </Typography>
              </Section>

              <Section title="12. EUROPEAN PRIVACY RIGHTS (GDPR)" colors={colors}>
                <Typography variant="body1" paragraph>
                  If you are in the EU/EEA, you have rights under GDPR including:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Right of access</li>
                    <li>Right to rectification</li>
                    <li>Right to erasure</li>
                    <li>Right to restrict processing</li>
                    <li>Right to data portability</li>
                    <li>Right to object</li>
                  </ul>
                </Typography>
                <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                  Legal basis for processing:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Contract performance (providing subscription services)</li>
                    <li>Legitimate interests (improving services, preventing fraud)</li>
                    <li>Consent (where required)</li>
                  </ul>
                </Typography>
              </Section>

              <Section title="13. DO NOT TRACK" colors={colors}>
                <Typography variant="body1" paragraph>
                  The App does not respond to "Do Not Track" signals as there is no industry standard for mobile apps.
                </Typography>
              </Section>

              <Section title="14. CHANGES TO THIS POLICY" colors={colors}>
                <Typography variant="body1" paragraph>
                  We may update this Privacy Policy periodically. We will notify you of material changes through the App. Your continued use after changes constitutes acceptance.
                </Typography>
              </Section>

              <Section title="15. CONTACT US" colors={colors}>
                <Typography variant="body1" paragraph>
                  For privacy-related questions, concerns, or requests, contact us at:
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
                  Email: cem@plotcamp.com
                </Typography>
                <Typography variant="body1" paragraph>
                  You may also contact us to:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>Request access to your data</li>
                    <li>Request deletion of your data</li>
                    <li>Report a privacy concern</li>
                    <li>Exercise your privacy rights</li>
                  </ul>
                </Typography>
              </Section>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
  colors: any;
}

const Section: React.FC<SectionProps> = ({ title, children, colors }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" sx={{ mb: 2, fontWeight: 400, color: colors.text }}>
      {title}
    </Typography>
    {children}
  </Box>
);

interface SubSectionProps {
  title: string;
  children: React.ReactNode;
  colors: any;
}

const SubSection: React.FC<SubSectionProps> = ({ title, children, colors }) => (
  <Box sx={{ mb: 2, ml: 2 }}>
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 400, color: colors.text }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default PrivacyPolicy;
